export interface FetchJSONResult<T> {
  /**
   * The fetched data, or `undefined` if there was an error or no data.
   */
  data: T | undefined
  /**
   * Any error encountered during the fetch defined by a status code outside the
   * 200 - 299 range, or `undefined` if no error has occurred.
   */
  error?: Error
}

export interface FetchJSONOptions<T> {
  /**
   * The URL to fetch data from.
   */
  url: string
  /**
   * Optional configuration for the fetch request.  Corresponds to the
   * `RequestInit` object used by the Fetch API.
   */
  requestInit?: RequestInit
  /**
   * Optional callback that will be executed when the fetch completes successfully.
   *
   * @param data The fetched data.
   */
  onCompleted?: (data: T) => void
  /**
   * Optional callback that will be executed if the fetch fails.
   *
   * @param err The error response
   */
  onError?: (err: Error) => void
}

/**
 * A utility function to perform an imperative fetch request.  This function is
 * useful when you need to fetch data outside of a React hook.
 *
 * It automatically handles response errors and attempts to parse the response
 * as JSON.  If the response contains a message property in the body, it will be
 * included in the error message.
 *
 * @example
 * const {data, error }= await fetchJSON<User[]>({
 *   url: '/api/users',
 *   onCompleted: (data) => console.log('Users:', data),
 *   onError: (err) => console.error('Fetch failed:', err),
 * });
 *
 * if (error) {
 *   console.error('Failed to fetch:', error);
 * } else {
 *   console.log('Fetched data:', data);
 * }
 */
export async function fetchJSON<T = unknown>({
  url,
  requestInit,
  onError,
  onCompleted,
}: FetchJSONOptions<T>): Promise<FetchJSONResult<T>> {
  try {
    const res = await fetch(url, requestInit)
    if (!res.ok) {
      const errors = [`HTTP error with status: ${res.status}`]
      const contentType = res.headers.get('content-type')
      let responseBody: T | undefined

      if (contentType && contentType.includes('application/json')) {
        // Only try to parse as JSON if the content type is JSON
        try {
          responseBody = await res.json()
        } catch (jsonError) {
          // ignore
        }

        if (
          responseBody !== undefined &&
          responseBody !== null &&
          typeof responseBody === 'object' &&
          'message' in responseBody &&
          typeof responseBody.message === 'string'
        ) {
          errors.unshift(responseBody.message)
        }
      }

      throw new Error(errors.join('; '))
    }
    const data = await res.json()

    if (onCompleted) {
      onCompleted(data)
    }
    return { data }
  } catch (e) {
    const error = e instanceof Error ? e : new Error(`Unknown error: ${e}`)
    if (onError) {
      onError(error)
    }
    return { data: undefined, error }
  }
}
